// @flow
import React from 'react'
import { Link, useStaticQuery, graphql } from 'gatsby'
import { format } from 'date-fns'
import classNames from 'classnames'
import Excursion from './excursion.mdx'
import Button from './button'
import Icon from './icon'
import styles from './programme.module.css'
import { slidesLinkFor } from '../utils'
import programme from './programme.yml'

type Props = {
  isCompact: boolean,
}

function Programme({ isCompact }: Props) {
  const { allEntries, allSpeakers } = useStaticQuery(graphql`
    query Programme {
      allEntries: allMdx(
        filter: { fields: { sourceName: { in: ["workshops", "talks"] } } }
      ) {
        edges {
          node {
            fields {
              slug
              path
            }
            exports {
              meta {
                title
                speakers
                hasSlides
                tbd
              }
            }
          }
        }
      }
      allSpeakers: allMdx(
        filter: { fields: { sourceName: { eq: "speakers" } } }
      ) {
        edges {
          node {
            fields {
              slug
              path
            }
            exports {
              meta {
                name
                tbd
              }
            }
          }
        }
      }
    }
  `)

  const entries = allEntries.edges.map((edge) => ({
    ...edge.node.fields,
    ...edge.node.exports.meta,
  }))
  const speakers = allSpeakers.edges.map((edge) => ({
    ...edge.node.fields,
    ...edge.node.exports.meta,
  }))

  const entrySlugsMissingSpeakers = entries
    .filter((entry) => !entry.tbd && entry.speakers.length === 0)
    .map((entry) => entry.slug)
    .join(',\n')

  if (entrySlugsMissingSpeakers) {
    throw new Error(
      `The following entries don't have speakers assigned:\n\n${entrySlugsMissingSpeakers}`,
    )
  }

  return (
    <>
      <Button
        size="small"
        as="a"
        href="https://res.cloudinary.com/pubmet/image/upload/v1/programme.pdf"
        target="_blank"
        rel="noopener noreferrer"
        download
      >
        <Icon id="file-download" size={24} />
        <span>Programme</span>
      </Button>{' '}
      <Button
        size="small"
        as="a"
        href="https://res.cloudinary.com/pubmet/image/upload/v1/book-of-abstracts.pdf"
        target="_blank"
        rel="noopener noreferrer"
        download
      >
        <Icon id="file-download" size={24} />
        <span>Book of Abstracts</span>
      </Button>
      <ol className={styles.list}>
        {programme.map((section) => (
          <li key={section.date} className={styles.section}>
            <div className={styles.sectionTitle}>
              <time dateTime={section.date} className={styles.day}>
                {format(section.date, 'D MMMM')}
              </time>
              {section.location != null ? (
                <>
                  {' in '}
                  <span className={styles.location}>{section.location}</span>
                </>
              ) : null}
            </div>
            <ol className={styles.list}>
              {section.events.map((event) => (
                <li
                  key={`${event.startsAt}-${event.endsAt}`}
                  className={styles.event}
                >
                  <div className={styles.time}>
                    <time dateTime={`${section.date}T${event.startsAt}`}>
                      {event.startsAt}
                    </time>
                    –
                    {event.endsAt != null ? (
                      <time dateTime={`${section.date}T${event.endsAt}`}>
                        {event.endsAt}
                      </time>
                    ) : null}
                  </div>
                  <div>
                    <div
                      className={classNames(styles.name, {
                        [styles.isCategory]: event.entries != null,
                      })}
                    >
                      {event.name}
                    </div>
                    {event.chair != null ? (
                      <div className={styles.chair}>Chair: {event.chair}</div>
                    ) : null}
                    {event.entries != null ? (
                      <ol className={styles.entries}>
                        {event.entries
                          .map((slug) =>
                            entries.find((entry) => entry.slug === slug),
                          )
                          .map((entry) => (
                            <li key={entry.slug} className={styles.entry}>
                              <div className={styles.entryTitle}>
                                {!entry.tbd ? (
                                  <Link to={entry.path}>{entry.title}</Link>
                                ) : (
                                  entry.title
                                )}
                              </div>
                              <div className={styles.authors}>
                                {entry.speakers.reduce((acc, slug, i) => {
                                  const speaker = speakers.find(
                                    (s) => s.slug === slug,
                                  )
                                  const speakerNode = !speaker.tbd ? (
                                    <Link key={speaker.name} to={speaker.path}>
                                      {speaker.name}
                                    </Link>
                                  ) : (
                                    speaker.name
                                  )
                                  acc.push(speakerNode)
                                  if (i < entry.speakers.length - 1) {
                                    acc.push(', ')
                                  }
                                  return acc
                                }, [])}
                                {/* {entry.speakers.length > 0 ? ': ' : null} */}
                              </div>
                              {entry.hasSlides ? (
                                <div>
                                  <a
                                    className={styles.slides}
                                    href={slidesLinkFor(entry.title)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <Icon
                                      className={styles.icon}
                                      id="file-presentation"
                                      size={16}
                                    />{' '}
                                    Slides
                                  </a>
                                </div>
                              ) : null}
                            </li>
                          ))}
                        {event.hasDiscussion ? (
                          <li className={styles.discussion}>Discussion</li>
                        ) : null}
                      </ol>
                    ) : null}
                  </div>
                </li>
              ))}
              {section.tbd ? (
                <li className={styles.event}>
                  <div className={styles.time} />
                  <div>TBA</div>
                </li>
              ) : null}
            </ol>
          </li>
        ))}
      </ol>
      <div id="excursion" className={styles.excursion}>
        {isCompact ? (
          <>
            <p>
              On September 21st there is going to be{' '}
              <Link to="/programme#excursion">
                <b>an excursion</b>
              </Link>{' '}
              with a licensed tourist guide to a viewpoint at the natural park
              Vransko lake, followed by visiting Biograd na Moru, and ending
              with olive oil and wine tasting.
            </p>
          </>
        ) : (
          <Excursion />
        )}
      </div>
    </>
  )
}

Programme.defaultProps = {
  isCompact: false,
}

export default Programme
